import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { useDropboxFolders } from '../../hooks/useDropboxFolders';

interface NewFolderModalProps {
  parentId: string | null;
  onClose: () => void;
  onSuccess: () => void;
}

const NewFolderModal: React.FC<NewFolderModalProps> = ({ parentId, onClose, onSuccess }) => {
  const [folderName, setFolderName] = useState('');
  const [isGlobal, setIsGlobal] = useState(true); // Default to global (hidden from UI)
  const [validationError, setValidationError] = useState('');
  const { createNewFolder, loading } = useDropboxFolders();
  const folders = useSelector((state: any) => state.dropbox.folders);

  // Validate folder name uniqueness
  const validateFolderName = (name: string): boolean => {
    if (!name.trim()) {
      setValidationError('Folder name cannot be empty');
      return false;
    }

    // Check for duplicate names in the same parent folder
    const siblings = folders.filter((f: any) => 
      f.parent_id === parentId && !f.is_deleted
    );
    
    const isDuplicate = siblings.some((f: any) => 
      f.name.toLowerCase() === name.trim().toLowerCase()
    );

    if (isDuplicate) {
      setValidationError('A folder with this name already exists in this location');
      return false;
    }

    setValidationError('');
    return true;
  };

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newName = e.target.value;
    setFolderName(newName);
    if (newName.trim()) {
      validateFolderName(newName);
    } else {
      setValidationError('');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateFolderName(folderName)) {
      return;
    }

    const result = await createNewFolder(folderName.trim(), parentId, isGlobal);
    
    if (result) {
      onSuccess();
      onClose();
    }
  };

  return (
    <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
      <div className="modal-dialog">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">Create New Folder</h5>
            <button type="button" className="close" onClick={onClose}>
              <span>&times;</span>
            </button>
          </div>
          <form onSubmit={handleSubmit}>
            <div className="modal-body">
              <div className="form-group">
                <label>Folder Name</label>
                <input
                  type="text"
                  className={`form-control ${validationError ? 'is-invalid' : ''}`}
                  value={folderName}
                  onChange={handleNameChange}
                  placeholder="Enter folder name"
                  autoFocus
                  required
                />
                {validationError && (
                  <div className="invalid-feedback d-block">
                    {validationError}
                  </div>
                )}
              </div>
              {/* Global checkbox hidden - all folders are global by default */}
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={onClose} disabled={loading}>
                Cancel
              </button>
              <button type="submit" className="btn btn-primary" disabled={loading || !folderName.trim() || !!validationError}>
                {loading ? 'Creating...' : 'Create Folder'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default NewFolderModal;
